import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuItemMui from "@mui/material/MenuItem";
import { Add, DeleteForever } from "@mui/icons-material";
import { tokens } from "../../theme";
import dayjs, { utc } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";

const ReboundHammerTestDetails = ({ mainData, setMainData, isNonMobile }) => {
  const [rows, setRows] = useState([]);
  const [samplingCount, setSamplingCount] = useState(0);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Initial row data structure
  const initialRow = {
    count: samplingCount,
    description: "",
    compressiveStrength: "",
  };

  const reportTypeArray = [
    {
      value: "C - 16/20",
      label: "C - 16/20",
    },
    { value: "C - 12/15", label: "C - 12/15" },
    { value: "C - 20/25", label: "C - 20/25" },
    { value: "C - 25/30", label: "C - 25/30" },
  ];
  useEffect(() => {
    setMainData((pre) => ({
      ...pre,
      labTestsDataFromSubs: rows,
    }));
  }, [rows]);

  // Add new row
  const addRow = () => {
    const newCount =
      mainData.labTestsDataFromSubs.length > 0
        ? Math.max(...mainData.labTestsDataFromSubs.map((row) => row.count)) + 1
        : 1;
    setSamplingCount(newCount);
    setRows([
      ...mainData.labTestsDataFromSubs,
      { ...initialRow, count: newCount },
    ]);
  };

  // Remove row
  const removeRow = (count) => {
    console.log("remov ", count);
    setSamplingCount((pre) => pre - 1);
    setRows(mainData.labTestsDataFromSubs.filter((row) => row.count !== count));
  };

  const degreeOfCompactionVal = (field, ddValue, mddValue) => {
    if (mddValue === "" || mddValue === 0) {
      return 0;
    }

    return (ddValue / mddValue).toFixed(2);
  };

  // Handle input changes
  const handleInputChange = (count, field, value) => {
    const updatedRows = mainData.labTestsDataFromSubs.map((row) => {
      if (row.count === count) {
        const updatedRow = { ...row, [field]: value };
        return updatedRow;
      }
      return row;
    });

    setRows(updatedRows);
  };

  return (
    <Box>
      <Box>
        <Box display="flex" flexDirection="row" gap={1}>
          {" "}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer
              components={["DatePicker"]}
              sx={{
                paddingTop: "8px",
                paddingBottom: "8px",
                marginLeft: "15px",
              }}
            >
              <DatePicker
                label="Testing Date"
                size="small"
                format="YYYY-MM-DD"
                timezone="Asia/Colombo"
                value={
                  mainData.dateOfSiteVisit
                    ? dayjs(mainData.dateOfSiteVisit).tz("Asia/Colombo", true)
                    : dayjs(new Date()).tz("Asia/Colombo", true)
                }
                onChange={(newValue) => {
                  const colomboTime = newValue?.tz("Asia/Colombo", true);
                  setMainData((pre) => ({
                    ...pre,
                    dateOfSiteVisit: dayjs(colomboTime).format("YYYY-MM-DD"),
                  }));
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
          <TextField
            select
            label="Grade"
            size="small"
            defaultValue=""
            value={mainData.grade}
            onChange={(e) => {
              setMainData((pre) => ({
                ...pre,
                grade: e.target.value,
              }));
            }}
            sx={{
              marginTop: "7px",
              width: "150px",
            }}
          >
            {reportTypeArray?.map((option) => (
              <MenuItemMui key={option.value} value={option.value}>
                {option.label}
              </MenuItemMui>
            ))}
          </TextField>
          <TextField
            fullWidth
            variant="outlined"
            type="text"
            label="Cement"
            size="small"
            value={mainData.cement}
            onChange={(e) =>
              setMainData((pre) => ({
                ...pre,
                cement: e.target.value,
              }))
            }
            sx={{ marginTop: "7px", width: "200px" }}
          />
        </Box>
        <Button
          type="submit"
          color="info"
          startIcon={<Add />}
          variant="contained"
          onClick={addRow}
          sx={{ marginBottom: "15px", marginLeft: "15px" }}
        >
          Add Data
        </Button>
      </Box>
      <Divider sx={{ margin: "5px 0 5px 0" }} />
      {/* -----------------------------------------------------------------enter values---------------------------------- */}
      <Box>
        {mainData.labTestsDataFromSubs.map((row, index) => (
          <Box key={index}>
            <Box
              display="grid"
              gap={0.5}
              padding="2px"
              gridTemplateColumns="repeat(8, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 8",
                  color: "red",
                },
              }}
            >
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Location No
              </Typography>
              <Typography
                sx={{ gridColumn: "span 5" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Location
              </Typography>

              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Comp. Strength N/mm²
              </Typography>
            </Box>

            <Box
              display="grid"
              gap={0.5}
              padding="2px"
              gridTemplateColumns="repeat(8, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 8",
                },
              }}
            >
              <Typography
                variant="h6"
                textAlign="center"
                alignContent="center"
                sx={{ gridColumn: "span 1" }}
                borderRadius={1}
                border={1}
                color={colors.grey[700]}
              >
                {row.count}.
              </Typography>

              <TextField
                variant="outlined"
                sx={{ margin: "2px 0 5px 0", gridColumn: "span 5" }}
                type="text"
                label="Location"
                size="small"
                value={row.description}
                fullWidth
                onChange={(e) =>
                  handleInputChange(row.count, "description", e.target.value)
                }
              />

              <TextField
                variant="outlined"
                type="number"
                label="Com. Strength "
                size="small"
                value={row.compressiveStrength}
                onChange={(e) =>
                  handleInputChange(
                    row.count,
                    "compressiveStrength",
                    e.target.value
                  )
                }
                sx={{
                  gridColumn: "span 1",
                  marginTop: "2px",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />

              <IconButton onClick={() => removeRow(row.count)} color="error">
                <DeleteForever sx={{ fontSize: "25px" }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ReboundHammerTestDetails;
